import type { JSX, ReactNode } from 'react';

import type {
  FreigabeVorschau,
  RessourcenAnfrage,
  WorkPackageFachgruppeOption,
  WunschKandidat,
} from '@asas/domain';

import { stundenAnzeige } from '../wochenraster/wochenraster';

import {
  abweichungDetails,
  ausgangText,
  duplikatText,
  entscheidungsMeldung,
  formularMeldung,
  umfangText,
  wochenbereichText,
  wochenText,
} from './anfragen';
import type {
  AnfrageFormular,
  AnfrageHinweisAnzeige,
  EntscheidungsFehler,
  EntscheidungsFormular,
  FormularFehler,
} from './anfragen';

/**
 * Darstellung der anonymen Ressourcenanfrage (AC-404) — ohne Zustand und ohne
 * Netz; der Zustand liegt in `AnfrageBereich`, `MeineAnfragen` und
 * `OffeneAnfragen`.
 */

export interface AnfrageFormularAnsichtProps {
  readonly arbeitspaketName: string;
  readonly fachgruppen: readonly WorkPackageFachgruppeOption[];
  readonly kandidaten: readonly WunschKandidat[];
  readonly formular: AnfrageFormular;
  readonly gesperrt: boolean;
  /** Läuft gerade ein Senden? Nur das sperrt „Abbrechen" — nach einem 403 bleibt Schließen möglich. */
  readonly sendet: boolean;
  readonly fehlt: FormularFehler | null;
  /** Ein Hinweis des Servers (AK-6, AK-7); solange er offen ist, ist das Formular gesperrt. */
  readonly hinweis: AnfrageHinweisAnzeige | null;
  readonly onFormular: (formular: AnfrageFormular) => void;
  readonly onAbsenden: () => void;
  /** AK-7: „Ja, so anfragen" — erneut mit `umfangBestaetigt`. */
  readonly onUmfangBestaetigen: () => void;
  readonly onHinweisSchliessen: () => void;
  readonly onAbbrechen: () => void;
}

export function AnfrageFormularAnsicht(props: AnfrageFormularAnsichtProps): JSX.Element {
  const { formular, hinweis } = props;
  const gesperrt = props.gesperrt || hinweis !== null;
  const setze = (aenderung: Partial<AnfrageFormular>) => {
    props.onFormular({ ...formular, ...aenderung });
  };
  const fehlerId = 'anfrage-formular-fehler';

  return (
    <fieldset aria-label={`Ressourcen anfragen für ${props.arbeitspaketName}`}>
      <legend>Ressourcen anfragen · {props.arbeitspaketName}</legend>
      <p>
        <label>
          Fachgruppe{' '}
          <select
            required
            disabled={gesperrt}
            value={formular.fachgruppeId}
            aria-invalid={props.fehlt === 'fachgruppe'}
            aria-describedby={props.fehlt === 'fachgruppe' ? fehlerId : undefined}
            onChange={(e) => {
              setze({ fachgruppeId: e.target.value });
            }}
          >
            <option value="">Bitte wählen</option>
            {props.fachgruppen.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
      </p>
      <p>
        <label>
          Stundenumfang gesamt{' '}
          <input
            required
            inputMode="decimal"
            disabled={gesperrt}
            value={formular.stunden}
            aria-invalid={props.fehlt === 'stunden'}
            aria-describedby={props.fehlt === 'stunden' ? fehlerId : undefined}
            onChange={(e) => {
              setze({ stunden: e.target.value });
            }}
          />
        </label>
      </p>
      <p>
        <label>
          Von KW{' '}
          <input
            type="week"
            required
            disabled={gesperrt}
            value={formular.von}
            aria-invalid={props.fehlt === 'von'}
            aria-describedby={props.fehlt === 'von' ? fehlerId : undefined}
            onChange={(e) => {
              setze({ von: e.target.value });
            }}
          />
        </label>{' '}
        <label>
          bis KW{' '}
          <input
            type="week"
            required
            disabled={gesperrt}
            value={formular.bis}
            aria-invalid={props.fehlt === 'bis'}
            aria-describedby={props.fehlt === 'bis' ? fehlerId : undefined}
            onChange={(e) => {
              setze({ bis: e.target.value });
            }}
          />
        </label>
      </p>
      <p>
        <label>
          Mitarbeiterwunsch (optional, unverbindlich){' '}
          <select
            disabled={gesperrt}
            value={formular.wunschEmployeeId}
            onChange={(e) => {
              setze({ wunschEmployeeId: e.target.value });
            }}
          >
            <option value="">Kein Wunsch</option>
            {props.kandidaten.map((k) => (
              <option key={k.employeeId} value={k.employeeId}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
        <br />
        <small>
          Der Wunsch ist eine Anregung. Wer eingeplant wird, entscheidet die Fachgruppenleitung.
        </small>
      </p>
      {props.fehlt === null ? null : (
        <p id={fehlerId} role="alert">
          {formularMeldung(props.fehlt)}
        </p>
      )}

      {hinweis?.ergebnis === 'duplikat' && (
        <div role="alertdialog" aria-labelledby="anfrage-duplikat-titel">
          <p id="anfrage-duplikat-titel">
            <strong>Es gibt schon eine Anfrage</strong>
          </p>
          <p>{duplikatText(hinweis.bestehende)}</p>
          <button type="button" onClick={props.onHinweisSchliessen}>
            Zurück zum Formular
          </button>
        </div>
      )}
      {hinweis?.ergebnis === 'bestaetigung_noetig' && (
        <div role="alertdialog" aria-labelledby="anfrage-umfang-titel">
          <p id="anfrage-umfang-titel">
            <strong>Großer Umfang bestätigen</strong>
          </p>
          <p>{umfangText(formular.stunden, hinweis.schwelleStunden)}</p>
          <button type="button" disabled={props.gesperrt} onClick={props.onUmfangBestaetigen}>
            Ja, so anfragen
          </button>{' '}
          <button type="button" disabled={props.gesperrt} onClick={props.onHinweisSchliessen}>
            Umfang korrigieren
          </button>
        </div>
      )}

      <p>
        <button type="button" disabled={gesperrt} onClick={props.onAbsenden}>
          Anfrage senden
        </button>{' '}
        <button type="button" disabled={props.sendet} onClick={props.onAbbrechen}>
          Abbrechen
        </button>
      </p>
    </fieldset>
  );
}

export interface AnfragenListeProps {
  readonly titel: string;
  readonly anfragen: readonly RessourcenAnfrage[];
  readonly leerText: string;
  /** Eine Aktion je Zeile (AC-405: „Entscheiden", „Zurückziehen"); ohne: keine Spalte. */
  readonly aktion?: ((anfrage: RessourcenAnfrage) => ReactNode) | undefined;
}

const STATUS_TEXT = { angefragt: 'angefragt', freigegeben: 'freigegeben', abgelehnt: 'abgelehnt' };

/**
 * Anfragen als Tabelle. Projekt und Arbeitspaket stehen nur da, wenn der Server
 * sie mitgeschickt hat (`projects:read`, bei den offenen Anfragen seit AC-405
 * auch `resource_allocation:approve`); sonst „nicht sichtbar". Der Ausgang
 * (AC-405) nennt Person, Abweichung je Woche oder die Begründung. Leer ist ein
 * Hinweis, kein Fehler.
 */
export function AnfragenListe({
  titel,
  anfragen,
  leerText,
  aktion,
}: AnfragenListeProps): JSX.Element {
  if (anfragen.length === 0) return <p>{leerText}</p>;

  return (
    <table aria-label={titel}>
      <thead>
        <tr>
          <th>Projekt</th>
          <th>Arbeitspaket</th>
          <th>Fachgruppe</th>
          <th>Zeitraum</th>
          <th>Stunden</th>
          <th>Wunsch (unverbindlich)</th>
          <th>Status</th>
          <th>Ausgang</th>
          {aktion === undefined ? null : <th>Aktion</th>}
        </tr>
      </thead>
      <tbody>
        {anfragen.map((a) => (
          <tr key={a.anfrageId}>
            <td>
              {a.projekt === null ? 'nicht sichtbar' : `${a.projekt.nummer} ${a.projekt.name}`}
            </td>
            <td>{a.arbeitspaket?.name ?? 'nicht sichtbar'}</td>
            <td>{a.fachgruppeName}</td>
            <td>{wochenbereichText(a.von, a.bis)}</td>
            <td>{stundenAnzeige(a.stunden)} h</td>
            <td>{a.wunsch?.name ?? '–'}</td>
            <td>{STATUS_TEXT[a.freigabeStatus]}</td>
            <td>
              {a.ausgang === null ? (
                '–'
              ) : (
                <>
                  {ausgangText(a.ausgang)}
                  {abweichungDetails(a.ausgang).map((zeile) => (
                    <div key={zeile}>
                      <small>{zeile}</small>
                    </div>
                  ))}
                </>
              )}
            </td>
            {aktion === undefined ? null : <td>{aktion(a)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface EntscheidungAnsichtProps {
  readonly anfrage: RessourcenAnfrage;
  /** Die Mitglieder der angefragten Fachgruppe (`fachgruppen.get`). */
  readonly kandidaten: readonly WunschKandidat[];
  readonly formular: EntscheidungsFormular;
  readonly vorschau: FreigabeVorschau | null;
  /** AK 4: die Warnung des Servers — gespeichert wurde nichts, bis bestätigt wird. */
  readonly ueberlast: FreigabeVorschau | null;
  readonly gesperrt: boolean;
  /** Läuft gerade ein Aufruf? Nur das sperrt „Schließen" — nach einem Konflikthinweis nicht. */
  readonly sendet: boolean;
  readonly fehlt: EntscheidungsFehler | null;
  readonly onFormular: (formular: EntscheidungsFormular) => void;
  readonly onVorschau: () => void;
  readonly onFreigeben: () => void;
  readonly onUeberlastBestaetigen: () => void;
  readonly onUeberlastVerwerfen: () => void;
  readonly onAblehnen: () => void;
  readonly onAbbrechen: () => void;
}

const ZUSTAND_TEXT = { abwesend: 'abwesend', keine_kapazitaet: 'keine Kapazität' } as const;

function auslastungText(woche: FreigabeVorschau['wochen'][number]): string {
  const { auslastung } = woche;
  const zustand =
    auslastung.zustand === 'prozent'
      ? `${String(auslastung.prozent)} %`
      : ZUSTAND_TEXT[auslastung.zustand];

  return woche.ueberlast ? `${zustand} – Überlast` : zustand;
}

/**
 * Eine Anfrage entscheiden (AC-405): Person wählen, je Woche Stunden setzen,
 * Wochen herausnehmen oder hinzufügen (AK 2, AK 7), die Wirkung ansehen
 * (AK 4), freigeben oder mit Begründung ablehnen (AK 3). Abwesenheiten
 * erscheinen nur als Stunden, nie mit Art (Kernregel 28).
 */
export function EntscheidungAnsicht(props: EntscheidungAnsichtProps): JSX.Element {
  const { anfrage, formular, vorschau, ueberlast } = props;
  const gesperrt = props.gesperrt || ueberlast !== null;
  const setze = (aenderung: Partial<EntscheidungsFormular>) => {
    props.onFormular({ ...formular, ...aenderung });
  };
  const setzeWoche = (
    index: number,
    aenderung: Partial<EntscheidungsFormular['wochen'][number]>,
  ) => {
    setze({ wochen: formular.wochen.map((w, i) => (i === index ? { ...w, ...aenderung } : w)) });
  };
  const fehlerId = `entscheidung-fehler-${anfrage.anfrageId}`;

  return (
    <fieldset aria-label="Anfrage entscheiden">
      <legend>
        Anfrage entscheiden · {wochenbereichText(anfrage.von, anfrage.bis)},{' '}
        {stundenAnzeige(anfrage.stunden)} h
      </legend>
      {anfrage.wunsch === null ? null : (
        <p>
          <small>Wunsch der Projektleitung (unverbindlich): {anfrage.wunsch.name}</small>
        </p>
      )}
      <p>
        <label>
          Person{' '}
          <select
            required
            disabled={gesperrt}
            value={formular.employeeId}
            aria-invalid={props.fehlt === 'person'}
            aria-describedby={props.fehlt === 'person' ? fehlerId : undefined}
            onChange={(e) => {
              setze({ employeeId: e.target.value });
            }}
          >
            <option value="">Bitte wählen</option>
            {props.kandidaten.map((k) => (
              <option key={k.employeeId} value={k.employeeId}>
                {k.name}
              </option>
            ))}
          </select>
        </label>
      </p>
      <table aria-label="Wochen der Freigabe">
        <thead>
          <tr>
            <th>Kalenderwoche</th>
            <th>Stunden</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {formular.wochen.map((w, index) => (
            <tr key={index}>
              <td>
                <input
                  type="week"
                  aria-label={`Kalenderwoche ${String(index + 1)}`}
                  disabled={gesperrt}
                  value={w.woche}
                  aria-invalid={props.fehlt === 'wochen'}
                  onChange={(e) => {
                    setzeWoche(index, { woche: e.target.value });
                  }}
                />
              </td>
              <td>
                <input
                  inputMode="decimal"
                  aria-label={`Stunden ${String(index + 1)}`}
                  disabled={gesperrt}
                  value={w.stunden}
                  aria-invalid={props.fehlt === 'stunden'}
                  onChange={(e) => {
                    setzeWoche(index, { stunden: e.target.value });
                  }}
                />
              </td>
              <td>
                <button
                  type="button"
                  disabled={gesperrt}
                  onClick={() => {
                    setze({ wochen: formular.wochen.filter((_, i) => i !== index) });
                  }}
                >
                  Woche entfernen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        <button
          type="button"
          disabled={gesperrt}
          onClick={() => {
            setze({ wochen: [...formular.wochen, { woche: '', stunden: '' }] });
          }}
        >
          Woche hinzufügen
        </button>
      </p>
      {props.fehlt === null ? null : (
        <p id={fehlerId} role="alert">
          {entscheidungsMeldung(props.fehlt)}
        </p>
      )}

      {/* Bei offener Warnung zeigt der Dialog die Wirkung — nicht ein zweites Mal hier. */}
      {vorschau === null || ueberlast !== null ? null : <VorschauTabelle vorschau={vorschau} />}

      {ueberlast === null ? null : (
        <div role="alertdialog" aria-labelledby={`ueberlast-${anfrage.anfrageId}`}>
          <p id={`ueberlast-${anfrage.anfrageId}`}>
            <strong>Überlast in {wochenText(ueberlast.ueberlastWochen)}</strong>
          </p>
          <VorschauTabelle vorschau={ueberlast} />
          <p>
            Die Freigabe ist trotzdem möglich; die Überlast wird mit der Entscheidung gespeichert.
          </p>
          <button type="button" disabled={props.gesperrt} onClick={props.onUeberlastBestaetigen}>
            Trotzdem freigeben
          </button>{' '}
          <button type="button" disabled={props.gesperrt} onClick={props.onUeberlastVerwerfen}>
            Zurück zur Freigabe
          </button>
        </div>
      )}

      <p>
        <button type="button" disabled={gesperrt} onClick={props.onVorschau}>
          Wirkung prüfen
        </button>{' '}
        <button type="button" disabled={gesperrt} onClick={props.onFreigeben}>
          Freigeben
        </button>
      </p>
      <p>
        <label>
          Begründung der Ablehnung{' '}
          <textarea
            disabled={gesperrt}
            maxLength={1000}
            value={formular.begruendung}
            aria-invalid={props.fehlt === 'begruendung'}
            aria-describedby={props.fehlt === 'begruendung' ? fehlerId : undefined}
            onChange={(e) => {
              setze({ begruendung: e.target.value });
            }}
          />
        </label>{' '}
        <button type="button" disabled={gesperrt} onClick={props.onAblehnen}>
          Ablehnen
        </button>
      </p>
      <p>
        <button type="button" disabled={props.sendet} onClick={props.onAbbrechen}>
          Schließen
        </button>
      </p>
    </fieldset>
  );
}

/** Die Wirkung je Woche (AK 4): verbindlich und Akquise getrennt, Abwesenheit nur in Stunden. */
function VorschauTabelle({ vorschau }: { readonly vorschau: FreigabeVorschau }): JSX.Element {
  return (
    <table aria-label="Wirkung der Freigabe">
      <thead>
        <tr>
          <th>KW</th>
          <th>Diese Freigabe</th>
          <th>Verbindlich geplant</th>
          <th>Akquise</th>
          <th>Soll</th>
          <th>Abwesend</th>
          <th>Verfügbar</th>
          <th>Auslastung</th>
        </tr>
      </thead>
      <tbody>
        {vorschau.wochen.map((w) => (
          <tr key={`${String(w.woche.isoYear)}-${String(w.woche.isoWeek)}`}>
            <td>{wochenbereichText(w.woche, w.woche)}</td>
            <td>
              {stundenAnzeige(w.freigabeHours)} h{w.belastung === 'akquise' ? ' (Akquise)' : ''}
            </td>
            <td>{stundenAnzeige(w.geplantHours)} h</td>
            <td>{stundenAnzeige(w.akquiseHours)} h</td>
            <td>{stundenAnzeige(w.sollHours)} h</td>
            <td>{stundenAnzeige(w.abwesendHours)} h</td>
            <td>{stundenAnzeige(w.verfuegbarHours)} h</td>
            <td>{auslastungText(w)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
